import {
  Box,
  Container,
  Dialog,
  Fab,
  Stack,
  Typography,
  Divider,
  CircularProgress,
  Paper,
  Grid,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import { AgentDetailModel } from "../../models/agent.model";
import ChatUI from "../Chat";
import { useParams, useSearchParams } from "react-router-dom";
import { getAgentById, updateAgentDetails } from "../../services/agent.service";
import { useDisclosure } from "@mantine/hooks";
import ChatIcon from "@mui/icons-material/Chat";
import { Edit, Save } from "@mui/icons-material";
import { get } from "http";
import {
  campaigns,
  getInfoLabel,
  getCampaignLabel,
} from "../../utilities/data.utility";
import { InformationDetailModel } from "../../models/information.model";
import { useCollectedInformation } from "../hooks/collectedinformation.hooks";
function ViewAgent() {
  const { agentId } = useParams();
  const {
    data: infoOptions,
    isLoading: isCollectedInformationLoading,
    isEmpty,
  } = useCollectedInformation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agentDetails, setAgentDetails] =
    React.useState<AgentDetailModel | null>(null);
  const [isLoading, { open: setLoading, close: stopLoading }] =
    useDisclosure(false);
  const [openChat, { open: openChatDialog, close: closeChatDialog }] =
    useDisclosure(false);
  const [isSaving, { open: setSaving, close: unsetSaving }] =
    useDisclosure(false);

  const fetchAndLoadAgentDetails = useCallback(async () => {
    if (!agentId) return;
    setLoading();
    try {
      const response = await getAgentById(agentId);
      setAgentDetails(response);
    } catch (error) {
      alert("Failed to load agent details. Please try again.");
      console.error("Error fetching agent details:", error);
      setAgentDetails(null);
    } finally {
      stopLoading();
    }
  }, [agentId]);

  const onSaveAgentDetails = useCallback(async () => {
    if (!agentId || !agentDetails) return;
    setSaving();
    try {
      // Assume updateAgentDetails is a service function to update agent details
      await updateAgentDetails(agentId, agentDetails);
      alert("Agent details saved successfully.");
      // Optionally, you can refetch the agent details here
      //fetchAndLoadAgentDetails();

      setSearchParams((prevSearchparams) => {
        const { edit, ...rest } = Object.fromEntries(prevSearchparams);
        return rest;
      });
    } catch (error) {
      alert("Failed to save agent details. Please try again.");
      console.error("Error saving agent details:", error);
    } finally {
      unsetSaving();
    }
  }, [agentId, agentDetails]);

  useEffect(() => {
    fetchAndLoadAgentDetails();
  }, [agentId]);

  const EditButtonComp = useMemo(
    () => (
      <EditButton
        onClick={() => {
          setSearchParams({ edit: "true" });
        }}
      />
    ),
    [agentId],
  );

  const SaveButtonComp = (
    <SaveButton
      onClick={() => {
        onSaveAgentDetails();
      }}
      isLoading={isSaving}
    />
  );

  const isEditing = () => {
    return searchParams.get("edit") === "true";
  };

  const getActionButton = () => {
    if (isEditing()) {
      return SaveButtonComp;
    } else {
      return EditButtonComp;
    }
  };

  if (isLoading)
    return (
      <Box
        sx={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress color="success" />
      </Box>
    );

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 }, width: "100%", maxWidth: 1440, mx: "auto" }}>
      <Stack alignItems={"flex-start"} sx={{ mb: 4 }} spacing={1}>
        <Typography variant="h5" fontWeight={600} sx={{ textAlign: "left" }}>
          Agent details
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", fontSize: 16, textAlign: "left" }}
        >
          Chat with your agent in real-time to explore its capabilities, test
          instructions, and experiment with different scenarios to see how it
          responds.
        </Typography>
        <Divider
          sx={{
            width: "100%",
          }}
        />
      </Stack>

      <Grid container spacing={3}>
        {/* Left Section: Agent Info */}
        <Grid size={{ xs: 12, md: 7.3 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <CardContent>
              <Stack spacing={3} mt={1}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 0.5 }}
                  >
                    Name
                  </Typography>
                  <Typography variant="h6" fontWeight={500}>
                    {agentDetails?.name || "N/A"}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={"space-between"}
                    spacing={1}
                    mb={1}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "text.secondary", mb: 0.5, fontSize: 18 }}
                    >
                      Instructions
                    </Typography>

                    {getActionButton()}
                  </Stack>

                  {isEditing() ? (
                    <TextField
                      disabled={isLoading}
                      label="Agent Script / Instructions"
                      name="script"
                      value={agentDetails?.instructions}
                      onChange={(e) =>
                        setAgentDetails((prev) =>
                          prev
                            ? { ...prev, instructions: e.target.value }
                            : prev,
                        )
                      }
                      fullWidth
                      required
                      multiline
                      rows={20}
                    />
                  ) : (
                    <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                      <Typography
                        variant="body1"
                        sx={{
                          lineHeight: 1.6,
                          textAlign: "left",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {agentDetails?.instructions || "N/A"}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={"space-between"}
                    spacing={1}
                    mb={1}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "text.secondary", mb: 0.5, fontSize: 18 }}
                    >
                      Campaign
                    </Typography>
                    {getActionButton()}
                  </Stack>

                  {isEditing() ? (
                    <TextField
                      select
                      disabled={isLoading}
                      label="Connect to Campaign"
                      name="campaign"
                      value={agentDetails?.campaign}
                      onChange={(e) =>
                        setAgentDetails((prev) =>
                          prev ? { ...prev, campaign: e.target.value } : prev,
                        )
                      }
                      fullWidth
                      required
                    >
                      {campaigns.map((c) => (
                        <MenuItem
                          key={c.value}
                          value={c.value}
                          disabled={isLoading}
                        >
                          {c.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        bgcolor: "grey.50",
                        p: 1.5,
                        borderRadius: 2,
                        lineHeight: 1.5,
                      }}
                    >
                      {getCampaignLabel(agentDetails?.campaign) || "N/A"}
                    </Typography>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={"space-between"}
                    spacing={1}
                    mb={1}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "text.secondary", mb: 0.5, fontSize: 18 }}
                    >
                      Information to Collect
                    </Typography>
                    {getActionButton()}
                  </Stack>

                  {isEditing() ? (
                    <>
                      {isCollectedInformationLoading ? (
                        <CircularProgress />
                      ) : (
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mt: 2 }}
                        >
                          {infoOptions?.map((opt: InformationDetailModel) => {
                            const selected = agentDetails?.informationToCollect
                              ?.map((info) => info.propertyKey)
                              ?.includes(opt.propertyKey as any as never);
                            return (
                              <Chip
                                disabled={isLoading}
                                key={opt.propertyKey}
                                label={opt.propertyLabel}
                                onClick={() => {
                                  if (!agentDetails) return;
                                  const currentInfo =
                                    agentDetails.informationToCollect || [];
                                  let updatedInfo: InformationDetailModel[] =
                                    [];
                                  if (selected) {
                                    // Deselect
                                    updatedInfo = currentInfo.filter(
                                      (info) =>
                                        info.propertyKey !== opt.propertyKey,
                                    );
                                  } else {
                                    // Select
                                    updatedInfo = [...currentInfo, opt];
                                  }
                                  setAgentDetails((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          informationToCollect: updatedInfo,
                                        }
                                      : prev,
                                  );
                                }}
                                color={selected ? "success" : "default"}
                                variant={selected ? "filled" : "outlined"}
                                sx={{
                                  cursor: "pointer",
                                }}
                              />
                            );
                          })}
                        </Stack>
                      )}
                    </>
                  ) : (
                    <Stack
                      spacing={1}
                      alignItems={"flex-start"}
                      justifyContent={"center"}
                    >
                      <Typography variant="body1">
                        {agentDetails?.informationToCollect?.length ? (
                          <>
                            {agentDetails.informationToCollect.map(
                              (info: InformationDetailModel) => (
                                <Chip
                                  key={info.propertyKey}
                                  label={getInfoLabel(
                                    infoOptions,
                                    info.propertyKey,
                                  )}
                                  color="success"
                                  sx={{ mt: 1, mr: 1 }}
                                />
                              ),
                            )}
                          </>
                        ) : (
                          "N/A"
                        )}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Section: Chat */}
        <Grid
          size={{ xs: 0, md: 4.7 }}
          sx={{
            display: {
              xs: "none",
              md: "flex",
            },
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          <ChatUI
            agentName={agentDetails?.name ?? "Agent"}
            agentId={agentId as string}
          />
        </Grid>
      </Grid>

      {/* Floating Chat Button for Mobile */}
      <Fab
        color="success"
        variant="extended"
        onClick={openChatDialog}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          borderRadius: 10,
          boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
          display: { xs: "flex", md: "none" },
        }}
      >
        <ChatIcon sx={{ mr: 1 }} />
        Chat with Agent
      </Fab>

      {/* Chat Dialog for Mobile */}
      <Dialog
        fullWidth
        maxWidth="md"
        open={openChat}
        onClose={closeChatDialog}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "transparent",
            },
            elevation: 0,
          },
        }}
      >
        <ChatUI
          agentName={agentDetails?.name ?? "Agent"}
          agentId={agentId as string}
          onCloseClick={closeChatDialog}
        />
      </Dialog>
    </Box>
  );
}

function EditButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      size="small"
      startIcon={<Edit />}
      variant="outlined"
      onClick={onClick}
    >
      Edit
    </Button>
  );
}

function SaveButton({
  onClick,
  isLoading,
}: {
  onClick: () => void;
  isLoading: boolean;
}) {
  return (
    <Button
      size="small"
      startIcon={<Save />}
      variant="contained"
      onClick={onClick}
      color="success"
      loading={isLoading}
    >
      Save
    </Button>
  );
}

export default ViewAgent;
